import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useContent } from '../hooks/useContent';
import { deriveColor, deriveAdvancedColors } from '../utils/colorDerive';
import { useSite } from '../hooks/useSites';
import { useToast } from '../hooks/useToast';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import { useEditorPreview } from '../hooks/useEditorPreview';
import { getContentId } from '../types';
import { getTemplate, v2Starters, getStartersForLayout, ADVANCED_COLOR_KEYS } from '../data/templates';
import ColorInput from '../components/ColorInput';
// PreviewPanel is now global — handled by Layout
import '../styles/editor.css';
import '../styles/appearance.css';
import '../styles/v2-picker.css';

const HEADING_FONT_SUGGESTIONS = [
  'Playfair Display',
  'DM Serif Display',
  'Cormorant Garamond',
  'Libre Baskerville',
  'Montserrat',
  'Space Grotesk',
  'Barlow',
  'Bebas Neue',
  'Oswald',
  'Poppins',
  'Raleway',
  'Merriweather',
  'Lora',
  'Bitter',
  'Crimson Text',
];

const BODY_FONT_SUGGESTIONS = [
  'Inter',
  'DM Sans',
  'Outfit',
  'Source Sans 3',
  'Lato',
  'Open Sans',
  'Roboto',
  'Nunito',
  'Work Sans',
  'Karla',
  'Rubik',
  'Mulish',
  'IBM Plex Sans',
  'Noto Sans',
];

// ─── Color derivation helpers ────────────────────────────────────

// Colour derivation utilities imported from ../utils/colorDerive

/** Inject a Google Fonts <link> if not already present */
function loadGoogleFont(fontName: string) {
  if (!fontName) return;
  const encoded = encodeURIComponent(fontName);
  const id = `gfont-${encoded}`;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encoded.replace(/%20/g, '+')}:wght@400;600;700&display=swap`;
  document.head.appendChild(link);
}

// ─── Section divider shape picker ────────────────────────────────

const DIVIDER_SHAPES: { value: string; label: string; shapePath: string; fill: string }[] = [
  { value: 'none',    label: 'None',      shapePath: 'M0,10 H56',                                                                         fill: 'M0,10 H56 V20 H0 Z' },
  { value: 'wave',    label: 'Wave',      shapePath: 'M0,10 C7,6 14,14 21,10 C28,6 35,14 42,10 C49,6 53,14 56,10',                        fill: 'M0,10 C7,6 14,14 21,10 C28,6 35,14 42,10 C49,6 53,14 56,10 V20 H0 Z' },
  { value: 'curve',   label: 'Curve',     shapePath: 'M0,4 Q28,14 56,4',                                                                  fill: 'M0,4 Q28,14 56,4 V20 H0 Z' },
  { value: 'slant',   label: 'Slant ↗',  shapePath: 'M0,14 L56,4',                                                                       fill: 'M0,14 L56,4 V20 H0 Z' },
  { value: 'zigzag',  label: 'Zigzag',    shapePath: 'M0,12 L7,4 L14,12 L21,4 L28,12 L35,4 L42,12 L49,4 L56,12',                         fill: 'M0,12 L7,4 L14,12 L21,4 L28,12 L35,4 L42,12 L49,4 L56,12 V20 H0 Z' },
  { value: 'tilt',    label: 'Slant ↘',  shapePath: 'M0,4 L56,14',                                                                       fill: 'M0,4 L56,14 V20 H0 Z' },
  { value: 'rounded', label: 'Arch',      shapePath: 'M0,10 Q28,4 56,10',                                                                 fill: 'M0,10 Q28,4 56,10 V20 H0 Z' },
];

// ─── Hero style options per layout ───────────────────────────────

const HERO_STYLES: Record<string, { value: string; label: string }[]> = {
  standard: [
    { value: 'fullwidth', label: 'Full width' },
    { value: 'gradient', label: 'Gradient' },
    { value: 'compact', label: 'Compact' },
    { value: 'text-only', label: 'Text only' },
  ],
  split: [
    { value: 'split', label: 'Split' },
    { value: 'split-reversed', label: 'Split (reversed)' },
  ],
  sidebar: [
    { value: 'compact', label: 'Compact' },
  ],
  landing: [
    { value: 'fullwidth', label: 'Full width' },
    { value: 'centered', label: 'Centred' },
    { value: 'gradient', label: 'Gradient' },
  ],
};

// ─── V2 Color field definitions ──────────────────────────────────

const V2_MAIN_COLOR_FIELDS = [
  { key: 'colorPrimary', label: 'Brand', desc: 'Buttons, links, and primary accents' },
  { key: 'colorBackground', label: 'Background', desc: 'Main page background' },
  { key: 'colorHeading', label: 'Headings', desc: 'Page titles and section headings' },
  { key: 'colorText', label: 'Text', desc: 'Body text and paragraphs' },
  { key: 'colorSecondary', label: 'Highlight', desc: 'Active states, hover accents, and decorative elements' },
];

const V2_ADVANCED_COLOR_FIELDS = [
  { key: 'colorPrimaryLight', label: 'Primary (light)', derive: 'lightenPrimary' },
  { key: 'colorAccent', label: 'Accent', derive: 'accent' },
  { key: 'colorBackgroundAlt', label: 'Background (alt)', derive: 'darkenBg' },
  { key: 'colorSurface', label: 'Surface', derive: 'surface' },
  { key: 'colorTextMuted', label: 'Text (muted)', derive: 'muteText' },
  { key: 'colorBorder', label: 'Borders', derive: 'border' },
  { key: 'colorFooterBg', label: 'Footer', derive: 'footerBg' },
];

const V2_GRADIENT_COLOR_FIELDS = [
  { key: 'heroGradientStart', label: 'Hero background' },
];

// ─── Helper: extract layout id from templateId ──────────────────

function getLayoutId(templateId: string): string {
  return templateId.replace('v2-', '');
}

// ─── Hover highlight selector mapping ────────────────────────────

const DESIGN_VAR_HIGHLIGHTS: Record<string, string> = {
  colorPrimary: 'button, a',
  colorBackground: 'body',
  colorHeading: 'h1, h2, h3, h4',
  colorText: 'p, li',
  colorSecondary: '[data-cms-section]',
  fontHeading: 'h1, h2, h3, h4',
  fontBody: 'p, li',
  navStyle: 'nav, header',
  heroStyle: '[data-cms-section="hero"]',
  heroPattern: '[data-hero-pattern]',
  sectionDivider: '[data-divider-shape]',
  ctaStyle: '[data-cms-section="cta"]',
  galleryStyle: '[data-cms-section="gallery"]',
  borderRadius: 'button, img',
  sectionSpacing: '[data-cms-section]',
  containerWidth: '.container, [class*="container"]',
};

// ─── V2 Design Variables Editor ─────────────────────────────────

function V2DesignEditor({
  designVars,
  onChange,
  layoutId,
  onHighlight,
}: {
  designVars: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  layoutId: string;
  onHighlight?: (selector: string | null) => void;
}) {
  const headingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bodyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showAdvancedColors, setShowAdvancedColors] = useState(false);

  const hoverOn = (key: string) => {
    const sel = DESIGN_VAR_HIGHLIGHTS[key];
    if (sel && onHighlight) onHighlight(sel);
  };
  const hoverOff = () => onHighlight?.(null);

  const getVar = (key: string, fallback = '') => (designVars[key] as string) ?? fallback;

  const heroStyle = getVar('heroStyle');
  const showGradientColors = heroStyle === 'gradient';

  // Derive advanced colors from the 3 main colors
  const primaryColor = getVar('colorPrimary', '#3b82f6');
  const bgColor = getVar('colorBackground', '#ffffff');
  const textColor = getVar('colorText', '#1a1a1a');

  const getDerivedOrCustom = (key: string, derive: string) => {
    const custom = getVar(key);
    if (custom) return custom;
    return deriveColor(derive, primaryColor, bgColor, textColor);
  };

  const handleFontChange = (field: string, value: string) => {
    onChange(field, value);
    const timerRef = field === 'fontHeading' ? headingTimerRef : bodyTimerRef;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (value) loadGoogleFont(value);
    }, 500);
  };

  const handleFontChip = (field: string, fontName: string) => {
    onChange(field, fontName);
    loadGoogleFont(fontName);
  };

  // Parse numeric from px/rem values
  const parseNumeric = (val: string, fallback: number) => {
    const n = parseFloat(val);
    return isNaN(n) ? fallback : n;
  };

  const headingFont = getVar('fontHeading', 'Inter');
  const bodyFont = getVar('fontBody', 'Inter');

  return (
    <>
      {/* ── Colours ── */}
      <div className="v2-design-section">
        <h2 className="v2-design-section-heading">Colours</h2>
        <div className="v2-color-grid">
          {V2_MAIN_COLOR_FIELDS.map(({ key, label, desc }) => (
            <div key={key} onMouseEnter={() => hoverOn(key)} onMouseLeave={hoverOff}>
              <ColorInput
                label={label}
                value={getVar(key)}
                defaultValue={getVar(key) || '#888888'}
                templateDefault={getVar(key) || '#888888'}
                onChange={(v) => onChange(key, v)}
              />
              <span className="v2-design-hint">{desc}</span>
            </div>
          ))}
        </div>

        <button
          type="button"
          className="v2-advanced-toggle"
          onClick={() => setShowAdvancedColors(!showAdvancedColors)}
        >
          {showAdvancedColors ? 'Hide advanced colours' : 'Advanced colours'}
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: showAdvancedColors ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', marginLeft: 4 }}>
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>

        {showAdvancedColors && (
          <div className="v2-color-grid" style={{ marginTop: 12 }}>
            {V2_ADVANCED_COLOR_FIELDS.map(({ key, label, derive }) => (
              <ColorInput
                key={key}
                label={label}
                value={getVar(key)}
                defaultValue={getDerivedOrCustom(key, derive)}
                templateDefault={getDerivedOrCustom(key, derive)}
                onChange={(v) => onChange(key, v)}
              />
            ))}
          </div>
        )}

        {showGradientColors && (
          <>
            <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--color-gray-700)', margin: '20px 0 12px' }}>
              Hero colour
            </h3>
            <div className="v2-color-grid">
              {V2_GRADIENT_COLOR_FIELDS.map(({ key, label }) => (
                <ColorInput
                  key={key}
                  label={label}
                  value={getVar(key)}
                  defaultValue={getVar(key) || '#888888'}
                  templateDefault={getVar(key) || '#888888'}
                  onChange={(v) => onChange(key, v)}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* ── Fonts ── */}
      <div className="v2-design-section">
        <h2 className="v2-design-section-heading">Fonts</h2>

        <div className="font-field" onMouseEnter={() => hoverOn('fontHeading')} onMouseLeave={hoverOff}>
          <label className="font-field-label">Heading font</label>
          <input
            type="text"
            className="font-field-input"
            value={headingFont}
            placeholder="e.g. Playfair Display"
            onChange={(e) => handleFontChange('fontHeading', e.target.value)}
          />
          <div className="font-suggestions">
            {HEADING_FONT_SUGGESTIONS.map((f) => (
              <button
                key={f}
                type="button"
                className={`font-chip${headingFont === f ? ' active' : ''}`}
                onClick={() => handleFontChip('fontHeading', f)}
              >
                {f}
              </button>
            ))}
          </div>
          <div className="font-preview" style={{ fontFamily: `'${headingFont}', serif`, fontSize: '24px', fontWeight: 700 }}>
            The quick brown fox
          </div>
        </div>

        <div className="font-field" onMouseEnter={() => hoverOn('fontBody')} onMouseLeave={hoverOff}>
          <label className="font-field-label">Body font</label>
          <input
            type="text"
            className="font-field-input"
            value={bodyFont}
            placeholder="e.g. Inter"
            onChange={(e) => handleFontChange('fontBody', e.target.value)}
          />
          <div className="font-suggestions">
            {BODY_FONT_SUGGESTIONS.map((f) => (
              <button
                key={f}
                type="button"
                className={`font-chip${bodyFont === f ? ' active' : ''}`}
                onClick={() => handleFontChip('fontBody', f)}
              >
                {f}
              </button>
            ))}
          </div>
          <div className="font-preview" style={{ fontFamily: `'${bodyFont}', sans-serif`, fontSize: '16px' }}>
            The quick brown fox jumps over the lazy dog
          </div>
        </div>
      </div>

      {/* ── Layout ── */}
      <div className="v2-design-section">
        <h2 className="v2-design-section-heading">Layout</h2>

        {/* Hero style */}
        {HERO_STYLES[layoutId] && HERO_STYLES[layoutId].length > 1 && (
          <div className="v2-design-row" onMouseEnter={() => hoverOn('heroStyle')} onMouseLeave={hoverOff}>
            <label className="v2-design-label">Hero style</label>
            <span className="v2-design-desc">The large banner at the top of your homepage</span>
            <select
              className="v2-design-select"
              value={getVar('heroStyle')}
              onChange={(e) => onChange('heroStyle', e.target.value)}
            >
              {HERO_STYLES[layoutId].map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <span className="v2-design-hint">Rebuild preview to see this change</span>
          </div>
        )}

        {/* Hero pattern — split layout only (when no hero image) */}
        {layoutId === 'split' && (
          <div className="v2-design-row" onMouseEnter={() => hoverOn('heroPattern')} onMouseLeave={hoverOff}>
            <label className="v2-design-label">Hero shapes</label>
            <span className="v2-design-desc">Decorative shapes shown when no hero image is uploaded</span>
            <select
              className="v2-design-select"
              value={getVar('heroPattern', 'circles')}
              onChange={(e) => onChange('heroPattern', e.target.value)}
            >
              <option value="circles">Circles</option>
              <option value="diamonds">Diamonds</option>
              <option value="blobs">Blobs</option>
              <option value="squares">Squares</option>
              <option value="rings">Rings</option>
            </select>
            <span className="v2-design-hint">Rebuild preview to see this change</span>
          </div>
        )}

        {/* Nav style */}
        <div className="v2-design-row" onMouseEnter={() => hoverOn('navStyle')} onMouseLeave={hoverOff}>
          <label className="v2-design-label">Navigation style</label>
          <span className="v2-design-desc">How the top menu bar looks — transparent blends into the hero, solid has a visible background</span>
          <select
            className="v2-design-select"
            value={getVar('navStyle', 'transparent')}
            onChange={(e) => onChange('navStyle', e.target.value)}
          >
            <option value="transparent">Transparent</option>
            <option value="solid">Solid</option>
          </select>
          <span className="v2-design-hint">Rebuild preview to see this change</span>
        </div>

        {/* Section dividers */}
        <div className="v2-design-row" onMouseEnter={() => hoverOn('sectionDivider')} onMouseLeave={hoverOff}>
          <label className="v2-design-label">Section dividers</label>
          <span className="v2-design-desc">Decorative shapes between content sections and above the footer</span>
          <div className="divider-picker">
            {DIVIDER_SHAPES.map(({ value, label, shapePath, fill }) => {
              const selected = getVar('sectionDivider', 'none') === value;
              return (
                <button
                  key={value}
                  type="button"
                  className={`divider-picker-item${selected ? ' divider-picker-item--active' : ''}`}
                  onClick={() => onChange('sectionDivider', value)}
                  aria-pressed={selected}
                >
                  <svg className="divider-picker-svg" viewBox="0 0 56 20" preserveAspectRatio="none" aria-hidden="true">
                    <rect className="divider-picker-bg" width="56" height="20"/>
                    <path className="divider-picker-fill" d={fill}/>
                    <path className="divider-picker-line" d={shapePath} fill="none" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  <span className="divider-picker-label">{label}</span>
                </button>
              );
            })}
          </div>
          <span className="v2-design-hint">Rebuild preview to see this change</span>
        </div>

        {/* CTA banner style */}
        <div className="v2-design-row" onMouseEnter={() => hoverOn('ctaStyle')} onMouseLeave={hoverOff}>
          <label className="v2-design-label">CTA banner</label>
          <span className="v2-design-desc">The coloured banner at the bottom of your homepage</span>
          <select
            className="v2-design-select"
            value={getVar('ctaStyle', 'animated-two')}
            onChange={(e) => onChange('ctaStyle', e.target.value)}
          >
            <option value="animated-two">Animated gradient</option>
            <option value="static-two">Static gradient</option>
            <option value="static-one">Solid colour</option>
          </select>
          <span className="v2-design-hint">Rebuild preview to see this change</span>
        </div>

        {/* Gallery style — sidebar only */}
        {layoutId === 'sidebar' && (
          <div className="v2-design-row" onMouseEnter={() => hoverOn('galleryStyle')} onMouseLeave={hoverOff}>
            <label className="v2-design-label">Gallery style</label>
            <span className="v2-design-desc">Grid shows equal-sized images, masonry fits images to their natural shape</span>
            <select
              className="v2-design-select"
              value={getVar('galleryStyle', 'grid')}
              onChange={(e) => onChange('galleryStyle', e.target.value)}
            >
              <option value="grid">Grid</option>
              <option value="masonry">Masonry</option>
            </select>
            <span className="v2-design-hint">Rebuild preview to see this change</span>
          </div>
        )}

        {/* Border radius */}
        <div className="v2-design-row" onMouseEnter={() => hoverOn('borderRadius')} onMouseLeave={hoverOff}>
          <label className="v2-design-label">Border radius</label>
          <span className="v2-design-desc">How rounded the corners are on cards, buttons, and images</span>
          <div className="v2-design-slider-row">
            <input
              type="range"
              className="v2-design-slider"
              min="0"
              max="24"
              step="1"
              value={parseNumeric(getVar('borderRadius', '8px'), 8)}
              onChange={(e) => onChange('borderRadius', `${e.target.value}px`)}
            />
            <span className="v2-design-slider-value">
              {parseNumeric(getVar('borderRadius', '8px'), 8)}px
            </span>
          </div>
        </div>

        {/* Section spacing */}
        <div className="v2-design-row" onMouseEnter={() => hoverOn('sectionSpacing')} onMouseLeave={hoverOff}>
          <label className="v2-design-label">Section spacing</label>
          <span className="v2-design-desc">The vertical gap between content sections — more space feels airy, less feels compact</span>
          <div className="v2-design-slider-row">
            <input
              type="range"
              className="v2-design-slider"
              min="3"
              max="8"
              step="0.5"
              value={parseNumeric(getVar('sectionSpacing', '5rem'), 5)}
              onChange={(e) => onChange('sectionSpacing', `${e.target.value}rem`)}
            />
            <span className="v2-design-slider-value">
              {parseNumeric(getVar('sectionSpacing', '5rem'), 5)}rem
            </span>
          </div>
        </div>

        {/* Container width */}
        <div className="v2-design-row" onMouseEnter={() => hoverOn('containerWidth')} onMouseLeave={hoverOff}>
          <label className="v2-design-label">Container width</label>
          <span className="v2-design-desc">Maximum width of your content area — wider uses more screen space on large monitors</span>
          <div className="v2-design-slider-row">
            <input
              type="range"
              className="v2-design-slider"
              min="900"
              max="1400"
              step="50"
              value={parseNumeric(getVar('containerWidth', '1200px'), 1200)}
              onChange={(e) => onChange('containerWidth', `${e.target.value}px`)}
            />
            <span className="v2-design-slider-value">
              {parseNumeric(getVar('containerWidth', '1200px'), 1200)}px
            </span>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Main Page Component ─────────────────────────────────────────

export default function AppearancePage() {
  const { siteId } = useParams<{ siteId: string }>();
  const { site } = useSite(siteId);
  const { items, loading, create, update } = useContent(siteId, 'site');
  const { addToast } = useToast();
  const { injectDesignVariables, setPreviewDirty, highlightBySelector } = useEditorPreview('/');

  const [formData, setFormData] = useState<Record<string, unknown>>({});  const [originalData, setOriginalData] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);
  const [confirmStarter, setConfirmStarter] = useState<string | null>(null);

  // Debounce timer for live design variable injection
  const designVarTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce timers for font loading (V1 only)
  const headingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bodyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const existingItem = items[0];
  const isV2 = site?.templateId?.startsWith('v2-') ?? false;
  const layoutId = isV2 && site ? getLayoutId(site.templateId) : '';
  const template = site ? getTemplate(site.templateId) : getTemplate('coastal-v1');

  const isDirty = JSON.stringify(formData) !== JSON.stringify(originalData);
  useUnsavedChanges(isDirty);

  useEffect(() => { setPreviewDirty(isDirty); }, [isDirty, setPreviewDirty]);

  // Push design variable changes to preview iframe in real time
  useEffect(() => {
    if (designVarTimerRef.current) clearTimeout(designVarTimerRef.current);

    if (isV2) {
      const dv = (formData.designVariables as Record<string, unknown>) || {};
      // Auto-derive advanced colors for live preview when not explicitly set
      const withDerived = deriveAdvancedColors(dv);
      designVarTimerRef.current = setTimeout(() => {
        injectDesignVariables(withDerived);
      }, 100);
    } else {
      // V1: map legacy override fields onto the same CSS variable keys the
      // preview overlay already understands, so they get live-previewed too
      const vars: Record<string, unknown> = {
        colorPrimary: formData.overridePrimaryColor,
        colorSecondary: formData.overrideSecondaryColor,
        colorBackground: formData.overrideBackgroundColor,
        colorText: formData.overrideTextColor,
        colorHeading: formData.overrideHeadingColor,
      };
      designVarTimerRef.current = setTimeout(() => {
        injectDesignVariables(vars);
      }, 100);
    }
    return () => {
      if (designVarTimerRef.current) clearTimeout(designVarTimerRef.current);
    };
  }, [
    isV2,
    formData.designVariables,
    formData.overridePrimaryColor,
    formData.overrideSecondaryColor,
    formData.overrideBackgroundColor,
    formData.overrideTextColor,
    formData.overrideHeadingColor,
    injectDesignVariables,
  ]);

  useEffect(() => {
    if (existingItem) {
      const data = existingItem.data || {};
      setFormData(data);
      setOriginalData(data);
    }
  }, [existingItem]);

  // Load fonts on mount from existing data
  useEffect(() => {
    if (isV2) {
      const dv = (formData.designVariables as Record<string, unknown>) || {};
      const h = (dv.fontHeading as string) || 'Inter';
      const b = (dv.fontBody as string) || 'Inter';
      if (h) loadGoogleFont(h);
      if (b) loadGoogleFont(b);
    } else {
      const headingFont = (formData.overrideHeadingFont as string) || template.fonts.heading;
      const bodyFont = (formData.overrideBodyFont as string) || template.fonts.body;
      if (headingFont) loadGoogleFont(headingFont);
      if (bodyFont) loadGoogleFont(bodyFont);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingItem]);

  // Preload all suggestion fonts for dropdown preview
  useEffect(() => {
    if (!isV2) return;
    [...HEADING_FONT_SUGGESTIONS, ...BODY_FONT_SUGGESTIONS].forEach(loadGoogleFont);
  }, [isV2]);

  const handleChange = useCallback((name: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  // V2: update a single design variable
  const handleDesignVarChange = useCallback((key: string, value: unknown) => {
    setFormData((prev) => {
      const existing = (prev.designVariables as Record<string, unknown>) || {};
      return {
        ...prev,
        designVariables: { ...existing, [key]: value },
      };
    });
  }, []);

  // V2: replace all design variables with a starter's defaults
    const handleStarterSwitch = useCallback((starterId: string) => {
    const starter = v2Starters.find((s) => s.id === starterId);
    if (!starter) return;
    // Strip advanced color keys so they auto-derive from the 3 main colours
    const vars: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(starter.variables)) {
      if (!ADVANCED_COLOR_KEYS.has(k)) {
        vars[k] = v;
      }
    }
    setFormData((prev) => ({
      ...prev,
      designVariables: vars,
    }));
    setConfirmStarter(null);
  }, []);

  const handleFontChange = useCallback(
    (field: 'overrideHeadingFont' | 'overrideBodyFont', value: string) => {
      handleChange(field, value);
      const timerRef = field === 'overrideHeadingFont' ? headingTimerRef : bodyTimerRef;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        if (value) loadGoogleFont(value);
      }, 500);
    },
    [handleChange],
  );

  const handleFontChip = useCallback(
    (field: 'overrideHeadingFont' | 'overrideBodyFont', fontName: string) => {
      handleChange(field, fontName);
      loadGoogleFont(fontName);
    },
    [handleChange],
  );

  const handleSave = async () => {
    if (!siteId) return;
    setSaving(true);
    try {
      // For V2 sites, auto-derive advanced colours before saving so
      // the built site gets them too (not just the live preview)
      let dataToSave = formData;
      if (isV2 && formData.designVariables) {
        const dv = formData.designVariables as Record<string, unknown>;
        const withDerived = deriveAdvancedColors(dv);
        dataToSave = { ...formData, designVariables: withDerived };
      }
      if (existingItem) {
        const contentId = getContentId(existingItem.contentKey);
        await update(contentId, dataToSave);
      } else {
        await create(dataToSave);
      }
      setOriginalData({ ...dataToSave });
      addToast('success', 'Appearance saved');
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="loading-state">
          <div className="spinner" />
        </div>
      </div>
    );
  }


  // ─── V2 Appearance ─────────────────────────────────────────────

  if (isV2) {
    const designVars = (formData.designVariables as Record<string, unknown>) || {};
    const layoutStarters = getStartersForLayout(layoutId);

    return (
      <div className="page">
        <div className="page-header">
          <h1>Appearance</h1>
          <div className="page-header-actions">
            {isDirty && <span className="unsaved-indicator">Unsaved changes</span>}
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

        {/* Starter quick-switch */}
        <div className="v2-design-section">
          <h2 className="v2-design-section-heading">Starting points</h2>
          <p className="v2-design-desc">Switch to a different preset. This replaces all current design settings.</p>
          <div className="v2-starter-switch">
            {layoutStarters.map((starter) => (
              <button
                key={starter.id}
                type="button"
                className={`v2-starter-chip${confirmStarter === starter.id ? ' v2-starter-chip--pending' : ''}`}
                onClick={() => setConfirmStarter(confirmStarter === starter.id ? null : starter.id)}
              >
                <span className="v2-starter-chip-swatches">
                  <span className="v2-starter-chip-swatch" style={{ background: (starter.variables.colorPrimary as string) || '#888' }} />
                  <span className="v2-starter-chip-swatch" style={{ background: (starter.variables.colorSecondary as string) || '#888' }} />
                  <span className="v2-starter-chip-swatch" style={{ background: (starter.variables.colorAccent as string) || '#888' }} />
                </span>
                {starter.name}
              </button>
            ))}
          </div>
          {confirmStarter && (() => {
            const starter = v2Starters.find((s) => s.id === confirmStarter);
            if (!starter) return null;
            return (
              <div className="v2-starter-confirm">
                <span className="v2-starter-confirm-text">Switch to {starter.name}? This replaces your current settings.</span>
                <div className="v2-starter-confirm-actions">
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setConfirmStarter(null)}>Cancel</button>
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => handleStarterSwitch(confirmStarter)}>Switch</button>
                </div>
              </div>
            );
          })()}
        </div>

        <V2DesignEditor
          designVars={designVars}
          onChange={handleDesignVarChange}
          layoutId={layoutId}
          onHighlight={highlightBySelector}
        />
      </div>
    );
  }

  // ─── V1 Appearance (unchanged) ─────────────────────────────────

  const headingFont = (formData.overrideHeadingFont as string) || template.fonts.heading;
  const bodyFont = (formData.overrideBodyFont as string) || template.fonts.body;

  return (
    <div className="page">
      <div className="page-header">
        <h1>Appearance</h1>
        <div className="page-header-actions">
          {isDirty && <span className="unsaved-indicator">Unsaved changes</span>}
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      <div className="appearance-settings">
        <div className="editor-main">
          <div className="appearance-settings">
          {/* Colors */}
          <div className="appearance-section">
            <h2 className="appearance-section-heading">Colors</h2>
            <ColorInput
              label="Primary Color"
              value={(formData.overridePrimaryColor as string) || ''}
              defaultValue={template.colors.primary}
              templateDefault={template.colors.primary}
              onChange={(v) => handleChange('overridePrimaryColor', v)}
            />
            <ColorInput
              label="Secondary Color"
              value={(formData.overrideSecondaryColor as string) || ''}
              defaultValue={template.colors.secondary}
              templateDefault={template.colors.secondary}
              onChange={(v) => handleChange('overrideSecondaryColor', v)}
            />
            <ColorInput
              label="Background"
              value={(formData.overrideBackgroundColor as string) || ''}
              defaultValue={template.colors.background}
              templateDefault={template.colors.background}
              onChange={(v) => handleChange('overrideBackgroundColor', v)}
            />
            <ColorInput
              label="Text Color"
              value={(formData.overrideTextColor as string) || ''}
              defaultValue={template.colors.text}
              templateDefault={template.colors.text}
              onChange={(v) => handleChange('overrideTextColor', v)}
            />
            <ColorInput
              label="Heading Color"
              value={(formData.overrideHeadingColor as string) || ''}
              defaultValue={template.colors.text}
              templateDefault={template.colors.text}
              onChange={(v) => handleChange('overrideHeadingColor', v)}
            />
          </div>

          {/* Fonts */}
          <div className="appearance-section">
            <h2 className="appearance-section-heading">Fonts</h2>

            {/* Heading Font */}
            <div className="font-field">
              <label className="font-field-label">Heading Font</label>
              <div className="font-field-row">
                <input
                  type="text"
                  className="font-field-input"
                  value={(formData.overrideHeadingFont as string) || ''}
                  placeholder={template.fonts.heading}
                  onChange={(e) => handleFontChange('overrideHeadingFont', e.target.value)}
                />
                {formData.overrideHeadingFont ? (
                  <button
                    type="button"
                    className="font-field-reset"
                    onClick={() => handleChange('overrideHeadingFont', '')}
                  >
                    Reset
                  </button>
                ) : null}
              </div>
              <span className="font-field-default">
                Template default: {template.fonts.heading}
              </span>
              <div
                className="font-preview"
                style={{ fontFamily: `'${headingFont}', serif`, fontSize: '24px', fontWeight: 700 }}
              >
                The quick brown fox
              </div>
              <div className="font-suggestions">
                {HEADING_FONT_SUGGESTIONS.map((f) => (
                  <button
                    key={f}
                    type="button"
                    className={`font-chip ${(formData.overrideHeadingFont as string) === f ? 'active' : ''}`}
                    onClick={() => handleFontChip('overrideHeadingFont', f)}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>

            {/* Body Font */}
            <div className="font-field">
              <label className="font-field-label">Body Font</label>
              <div className="font-field-row">
                <input
                  type="text"
                  className="font-field-input"
                  value={(formData.overrideBodyFont as string) || ''}
                  placeholder={template.fonts.body}
                  onChange={(e) => handleFontChange('overrideBodyFont', e.target.value)}
                />
                {formData.overrideBodyFont ? (
                  <button
                    type="button"
                    className="font-field-reset"
                    onClick={() => handleChange('overrideBodyFont', '')}
                  >
                    Reset
                  </button>
                ) : null}
              </div>
              <span className="font-field-default">
                Template default: {template.fonts.body}
              </span>
              <div
                className="font-preview"
                style={{ fontFamily: `'${bodyFont}', sans-serif`, fontSize: '16px' }}
              >
                The quick brown fox jumps over the lazy dog
              </div>
              <div className="font-suggestions">
                {BODY_FONT_SUGGESTIONS.map((f) => (
                  <button
                    key={f}
                    type="button"
                    className={`font-chip ${(formData.overrideBodyFont as string) === f ? 'active' : ''}`}
                    onClick={() => handleFontChip('overrideBodyFont', f)}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
}
